import React from 'react';

import { Label } from 'cpool-components/general/Label';
import { SegmentedControl } from 'cpool-components/input/SegmentedControl';
import type { FieldValues } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import TitleDateInput from './general/TitleDateInput.tsx';
import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useApplicantInfoContext } from '~/features/leadDetails/context/ApplicantInfoContext.tsx';

const OPTION_VALUES = {
    NO: 'NO',
    NOT_SPECIFIED: 'NOT_SPECIFIED',
    YES: 'YES',
} as const;

type Props = {
    probationaryPeriodPath: string;
    probationaryPeriodEndPath: string;
    disabled?: boolean;
};

const ProbationaryPeriod: React.FC<Props> = ({ disabled, probationaryPeriodEndPath, probationaryPeriodPath }) => {
    const { t } = useTranslation();
    const { applicantId } = useApplicantInfoContext();
    const { control } = useFormContext();
    const value = useWatch({
        name: probationaryPeriodPath,
    });

    const employmentPath = 'CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT';

    const options = [
        { label: t('VALUES.YES'), value: OPTION_VALUES.YES },
        { label: t('VALUES.NO'), value: OPTION_VALUES.NO },
        { label: t('VALUES.NOT_SPECIFIED'), value: OPTION_VALUES.NOT_SPECIFIED },
    ];

    const toOptionValue = (fieldValue: boolean | null | undefined) => {
        if (fieldValue === true) return OPTION_VALUES.YES;
        if (fieldValue === false) return OPTION_VALUES.NO;
        return OPTION_VALUES.NOT_SPECIFIED;
    };

    const toFieldValue = (optionValue: string) => {
        if (optionValue === OPTION_VALUES.YES) return true;
        if (optionValue === OPTION_VALUES.NO) return false;
        return null;
    };

    return (
        <>
            <FormField<FieldValues, boolean | null> control={control} name={probationaryPeriodPath}>
                {({ error, onChange, value: fieldValue }) => (
                    <div className="flex items-start gap-2.5">
                        <Label className="h-8 w-[175px] shrink-0 justify-start">
                            {t(`${employmentPath}.PROBATIONARY_PERIOD.TITLE`)}
                        </Label>
                        <SegmentedControl
                            id={`${probationaryPeriodPath}-${applicantId}`}
                            ariaLabel={t(`${employmentPath}.PROBATIONARY_PERIOD.TITLE`)}
                            options={options}
                            value={toOptionValue(fieldValue)}
                            onChange={(newValue) => onChange(toFieldValue(newValue))}
                            disabled={disabled}
                            error={error}
                            wide
                        />
                    </div>
                )}
            </FormField>
            {value && (
                <TitleDateInput
                    title={t(`${employmentPath}.PROBATIONARY_PERIOD.PROBATIONARY_PERIOD_END`)}
                    name={probationaryPeriodEndPath}
                    disabled={disabled}
                />
            )}
        </>
    );
};

export default ProbationaryPeriod;
